import { Badge, Group, Text } from '@mantine/core'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useTable } from './Table/useTable'
import { DetailedDriveStatistics } from '@/api/main/types.gen'
import { secondsToHHMM } from '@/utils/datetime'
import { Link } from '@tanstack/react-router'
import { formatDateTime } from '@/utils/datetime'
import { percentage, ratio } from '@/utils/math'
import { useMemo } from 'react'
import { useQuery, useQueries } from '@tanstack/react-query'
import { getAllActiveRoutesOptions, getRouteOptions } from '@/api/main'
import { createRouteNameMap } from '@/utils/routes'
import { useTranslation } from 'react-i18next'

interface DriveStatisticsTableProps {
  data: DetailedDriveStatistics[]
  isLoading?: boolean
  error?: Error | null
}

type DriveStatisticsWithSearchable = Omit<DetailedDriveStatistics, 'drivers'> & {
  drivers: string
  progress: number
  route_name?: string
}

const getBillingTypeColor = (type: string) => {
  switch (type) {
    case 'billable':
      return 'green'
    case 'non_billable':
      return 'gray'
    case 'development':
      return 'blue'
    case 'disrespect_all':
      return 'red'
    default:
      return 'gray'
  }
}

const formatDriveType = (types: string[]) => types.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ')
const formatRouteFragmentPercentage = (item: DriveStatisticsWithSearchable) =>
  item.start_index === 0 && item.end_index === item.route_plan_item_count
    ? '100%'
    : `${percentage(item.start_index, item.route_plan_item_count)}-${percentage(item.end_index, item.route_plan_item_count)}`

const DriveStatisticsTable = ({ data, isLoading = false, error = null }: DriveStatisticsTableProps) => {
  const { t } = useTranslation('statistics')
  const { data: allRoutes } = useQuery(getAllActiveRoutesOptions())

  const routeNameMap = useMemo(() => createRouteNameMap(allRoutes), [allRoutes])

  const missingRouteKeys = useMemo(() => {
    if (!data || !routeNameMap) return []
    const uniqueRoutes = new Map<string, { route_id: string; route_version: string }>()
    for (const item of data) {
      if (item.state !== 'pending' && item.state !== 'cancelled') {
        const routeKey = `${item.route_id}-${item.route_version}`
        if (!routeNameMap.has(routeKey)) {
          uniqueRoutes.set(routeKey, { route_id: item.route_id, route_version: item.route_version })
        }
      }
    }
    return Array.from(uniqueRoutes.entries()).map(([key, { route_id, route_version }]) => ({
      route_id,
      route_version,
      key,
    }))
  }, [data, routeNameMap])

  const missingRouteQueries = useQueries({
    queries: missingRouteKeys.map(({ route_id, route_version }) => ({
      ...getRouteOptions({
        path: {
          route_id,
          route_version,
        },
      }),
      enabled: missingRouteKeys.length > 0,
    })),
  })

  const fetchedRouteNames = useMemo(() => {
    const map = new Map<string, string | undefined>()
    missingRouteQueries.forEach((query, index) => {
      if (query.data) {
        const { key } = missingRouteKeys[index]
        map.set(key, query.data.name)
      }
    })
    return map
  }, [missingRouteQueries, missingRouteKeys])

  const completeRouteNameMap = useMemo(() => {
    const map = new Map(routeNameMap)
    fetchedRouteNames.forEach((name, key) => {
      map.set(key, name)
    })
    return map
  }, [routeNameMap, fetchedRouteNames])

  const transformedData = useMemo(
    () =>
      data
        .filter(item => item.state !== 'pending' && item.state !== 'cancelled')
        .map(item => {
          const routeKey = `${item.route_id}-${item.route_version}`
          const routeName = completeRouteNameMap.get(routeKey)
          return {
            ...item,
            drivers: item.drivers.length > 0 ? item.drivers.map(driver => driver.name).join(', ') : 'N/A',
            progress:
              item.max_driven_route_index !== undefined
                ? ratio(item.max_driven_route_index - item.start_index, item.end_index - item.start_index)
                : 0,
            route_name: routeName,
          }
        }),
    [data, completeRouteNameMap]
  )

  const defaultColumns: TableColumn<DriveStatisticsWithSearchable>[] = [
    {
      key: 'drive_id',
      label: t('driveId'),
      visible: true,
      sortable: true,
      align: 'left',
      render: (item: DriveStatisticsWithSearchable) => (
        <Link
          to="/drive-details/$id"
          params={{ id: item.drive_id.toString() }}
          style={{
            color: 'var(--mantine-color-blue-6)',
            textDecoration: 'underline',
          }}
        >
          {item.drive_id}
        </Link>
      ),
    },
    {
      key: 'vehicle_id',
      label: t('vehicle'),
      visible: true,
      sortable: true,
      align: 'left',
    },
    {
      key: 'drivers',
      label: t('driver'),
      visible: true,
      sortable: true,
      align: 'left',
      render: (item: DriveStatisticsWithSearchable) => (
        <Group gap="xs">
          {item.drivers.split(', ').map((driverName, index) => (
            <Badge key={index} variant="light" color={driverName !== 'N/A' ? 'blue' : 'gray'} size="sm">
              {driverName}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      key: 'planning_area_id',
      label: t('planningArea'),
      visible: true,
      sortable: true,
      align: 'left',
    },
    {
      key: 'route_name',
      label: t('routeName'),
      visible: true,
      sortable: true,
      align: 'left',
      render: (item: DriveStatisticsWithSearchable) => <Text>{item.route_name ?? 'N/A'}</Text>,
    },
    {
      key: 'route_id',
      label: t('route'),
      visible: true,
      sortable: true,
      align: 'left',
      render: (item: DriveStatisticsWithSearchable) => (
        <Link
          to="/route/$routeId/$version"
          params={{ routeId: item.route_id.toString(), version: item.route_version.toString() }}
          style={{
            color: 'var(--mantine-color-blue-6)',
            textDecoration: 'underline',
          }}
        >
          {item.route_id}
        </Link>
      ),
    },
    {
      key: 'state',
      label: t('status'),
      visible: true,
      sortable: true,
      align: 'left',
      render: (item: DriveStatisticsWithSearchable) => {
        const getStateColor = (state: string) => {
          switch (state) {
            case 'finished':
              return 'green'
            case 'in_progress':
              return 'blue'
            case 'pending':
              return 'yellow'
            case 'cancelled':
              return 'red'
            case 'cancelled_in_progress':
              return 'red'
            default:
              return 'gray'
          }
        }
        return (
          <Badge color={getStateColor(item.state)} variant="light" size="sm">
            {item.state.replace('_', ' ').toUpperCase()}
          </Badge>
        )
      },
    },
    {
      key: 'max_driven_route_index',
      label: t('progress'),
      visible: true,
      sortable: true,
      align: 'right',
      csvValue: item => percentage(item.progress, 1),
      render: (item: DriveStatisticsWithSearchable) => (
        <Text style={{ textAlign: 'right' }}>{percentage(item.progress, 1)}</Text>
      ),
    },
    {
      key: 'drive_started_at',
      label: t('startedAt'),
      visible: true,
      sortable: true,
      align: 'left',
      type: 'date',
      render: (item: DriveStatisticsWithSearchable) => {
        const date = item.drive_started_at
        if (!date || date === 'N/A')
          return (
            <Badge color="gray" variant="light">
              N/A
            </Badge>
          )
        return <Text>{formatDateTime(date)}</Text>
      },
    },
    {
      key: 'drive_finished_at',
      label: t('finishedAt'),
      visible: true,
      sortable: true,
      align: 'left',
      type: 'date',
      render: (item: DriveStatisticsWithSearchable) => {
        const date = item.drive_finished_at
        if (!date || date === 'N/A')
          return (
            <Badge color="gray" variant="light">
              N/A
            </Badge>
          )
        return <Text>{formatDateTime(date)}</Text>
      },
    },
    {
      key: 'duration_seconds',
      label: t('duration'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: DriveStatisticsWithSearchable) => secondsToHHMM(item.duration_seconds),
    },
    {
      key: 'route_plan_item_count',
      label: t('routeFragment'),
      visible: false,
      sortable: true,
      align: 'right',
      csvValue: item => formatRouteFragmentPercentage(item),
      render: (item: DriveStatisticsWithSearchable) => (
        <div style={{ textAlign: 'right' }}>{formatRouteFragmentPercentage(item)}</div>
      ),
    },
    {
      key: 'route_version',
      label: t('version'),
      visible: false,
      sortable: true,
      align: 'left',
    },
    {
      key: 'district_code',
      label: t('district'),
      visible: false,
      sortable: true,
      align: 'left',
      render: item => `${item.district_code} - ${item.subdistrict_code}`,
    },
    {
      key: 'drive_type',
      label: t('driveType'),
      visible: false,
      sortable: false,
      align: 'left',
      render: item => formatDriveType(item.drive_type),
    },
    {
      key: 'billing_type',
      label: t('billingType'),
      visible: false,
      sortable: true,
      align: 'left',
      render: item => (
        <Badge color={getBillingTypeColor(item.billing_type)} variant="light" size="sm">
          {item.billing_type.replace('_', ' ').toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'is_approved',
      label: t('approved'),
      visible: false,
      sortable: true,
      align: 'left',
      render: item => (
        <Badge color={item.is_approved ? 'green' : 'red'} variant="light" size="sm">
          {item.is_approved ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' })}
        </Badge>
      ),
    },
    {
      key: 'start_after',
      label: t('startAfter'),
      visible: false,
      sortable: true,
      align: 'left',
      render: item => formatDateTime(item.start_after),
    },
    {
      key: 'finish_before',
      label: t('finishBefore'),
      visible: false,
      sortable: true,
      align: 'left',
      render: item => formatDateTime(item.finish_before),
    },
    {
      key: 'unexpected_event_count',
      label: t('unexpectedEvents'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'skipped_segment_count',
      label: t('skippedSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'missed_segment_count',
      label: t('missedSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'detected_vehicle_count',
      label: t('detectedVehicles'),
      visible: true,
      sortable: true,
      align: 'right',
    },
    {
      key: 'detected_sign_post_count',
      label: t('detectedSignPosts'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'planned_route_segment_count',
      label: t('plannedRouteSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'required_route_segment_count',
      label: t('requiredRouteSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'driven_route_segment_count',
      label: t('drivenRouteSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'driven_planned_route_segment_count',
      label: t('drivenPlannedSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'driven_overhead_segment_count',
      label: t('drivenOverheadSegments'),
      visible: false,
      sortable: true,
      align: 'right',
    },
    {
      key: 'planned_route_segment_km',
      label: t('plannedRouteKm'),
      visible: false,
      sortable: true,
      align: 'right',
      render: item => `${item.planned_route_segment_km.toFixed(2)} km`,
    },
    {
      key: 'driven_route_segment_km',
      label: t('drivenRouteKm'),
      visible: false,
      sortable: true,
      align: 'right',
      render: item => `${item.driven_route_segment_km.toFixed(2)} km`,
    },
    {
      key: 'required_route_segment_km',
      label: t('requiredRouteKm'),
      visible: false,
      sortable: true,
      align: 'right',
      render: item => `${item.required_route_segment_km.toFixed(2)} km`,
    },
    {
      key: 'cancellation_reason',
      label: t('cancellationReason'),
      visible: false,
      sortable: false,
      align: 'left',
      render: item => item.cancellation_reason || 'N/A',
    },
  ]

  const table = useTable({
    data: transformedData,
    columns: defaultColumns,
    tableKey: 'drive-statistics-table',
    defaultSortField: 'drive_started_at',
    defaultSortDirection: 'desc',
    defaultFilterField: 'drive_id',
    itemsPerPage: 25,
    searchDebounceMs: 300,
  })

  return (
    <GenericTable
      {...table.tableProps}
      isLoading={isLoading}
      error={error}
      getRowKey={item => item.drive_id}
      emptyMessage={t('noDriveStatisticsAvailable')}
      configTitle={t('configureDriveStatisticsView')}
      csvFileName="drive-statistics.csv"
      horizontalSpacing="xs"
      verticalSpacing="xs"
      stickyHeader
    />
  )
}

export default DriveStatisticsTable
